/**
 * Pistas: constructores, semántica y pool de pistas verdaderas.
 *
 * El motor emite `kind` + `args`; los enunciados en castellano los produce el pack de
 * contenido. Aquí no hay ni una línea de narrativa a propósito.
 */

import { cellAt, firstHourOccupied, hourOf, lastHourOccupied } from './grid';
import type { Rng } from './rng';
import type { Clue, ClueKind, Grid } from './types';

/** Forma interna con campos nombrados; valida aridad y rangos al construirse. */
export type ParsedClue =
  | { kind: 'AT'; person: number; hour: number; room: number }
  | { kind: 'NOT_AT'; person: number; hour: number; room: number }
  | { kind: 'OPEN'; person: number; room: number }
  | { kind: 'CLOSE'; person: number; room: number }
  | { kind: 'FIRST_IN'; person: number; room: number }
  | { kind: 'LAST_IN'; person: number; room: number }
  | { kind: 'JUST_BEFORE'; first: number; second: number; room: number }
  | { kind: 'BEFORE'; first: number; second: number; room: number };

const ARGS_BY_KIND: Record<ClueKind, readonly string[]> = {
  AT: ['person', 'hour', 'room'],
  NOT_AT: ['person', 'hour', 'room'],
  OPEN: ['person', 'room'],
  CLOSE: ['person', 'room'],
  FIRST_IN: ['person', 'room'],
  LAST_IN: ['person', 'room'],
  JUST_BEFORE: ['person', 'person', 'room'],
  BEFORE: ['person', 'person', 'room'],
};

function makeClue(kind: ClueKind, args: number[]): Clue {
  const names = ARGS_BY_KIND[kind];
  const named = args.map((value, i) => `${names[i] ?? 'arg'}=${value}`).join(', ');
  return { id: `${kind}:${args.join(':')}`, kind, args, text: `${kind}(${named})` };
}

/**
 * Constructores de pista. Los usa el generador, y también el contenido y los tests
 * cuando necesitan escribir un caso a mano.
 */
export const clue = {
  at: (person: number, hour: number, room: number): Clue =>
    makeClue('AT', [person, hour, room]),
  notAt: (person: number, hour: number, room: number): Clue =>
    makeClue('NOT_AT', [person, hour, room]),
  open: (person: number, room: number): Clue => makeClue('OPEN', [person, room]),
  close: (person: number, room: number): Clue => makeClue('CLOSE', [person, room]),
  firstIn: (person: number, room: number): Clue => makeClue('FIRST_IN', [person, room]),
  lastIn: (person: number, room: number): Clue => makeClue('LAST_IN', [person, room]),
  justBefore: (first: number, second: number, room: number): Clue =>
    makeClue('JUST_BEFORE', [first, second, room]),
  before: (first: number, second: number, room: number): Clue =>
    makeClue('BEFORE', [first, second, room]),
};

function index(
  value: number | undefined,
  n: number,
  label: string,
  id: string,
): number {
  if (value === undefined || !Number.isInteger(value) || value < 0 || value >= n) {
    throw new RangeError(
      `pista ${id}: ${label} fuera de rango (0..${n - 1}): ${value}`,
    );
  }
  return value;
}

/** Valida una pista externa y la pasa a forma interna. */
export function parseClue(input: Clue, n: number): ParsedClue {
  const names = ARGS_BY_KIND[input.kind];
  if (names === undefined)
    throw new TypeError(`clase de pista desconocida: ${input.kind}`);
  if (input.args.length !== names.length) {
    throw new RangeError(
      `pista ${input.id}: ${input.kind} exige ${names.length} args, recibidos ${input.args.length}`,
    );
  }
  const a = (i: number, label: string): number =>
    index(input.args[i], n, label, input.id);

  switch (input.kind) {
    case 'AT':
      return {
        kind: 'AT',
        person: a(0, 'persona'),
        hour: a(1, 'hora'),
        room: a(2, 'sala'),
      };
    case 'NOT_AT':
      return {
        kind: 'NOT_AT',
        person: a(0, 'persona'),
        hour: a(1, 'hora'),
        room: a(2, 'sala'),
      };
    case 'OPEN':
      return { kind: 'OPEN', person: a(0, 'persona'), room: a(1, 'sala') };
    case 'CLOSE':
      return { kind: 'CLOSE', person: a(0, 'persona'), room: a(1, 'sala') };
    case 'FIRST_IN':
      return { kind: 'FIRST_IN', person: a(0, 'persona'), room: a(1, 'sala') };
    case 'LAST_IN':
      return { kind: 'LAST_IN', person: a(0, 'persona'), room: a(1, 'sala') };
    case 'JUST_BEFORE':
      return {
        kind: 'JUST_BEFORE',
        first: a(0, 'persona'),
        second: a(1, 'persona'),
        room: a(2, 'sala'),
      };
    case 'BEFORE':
      return {
        kind: 'BEFORE',
        first: a(0, 'persona'),
        second: a(1, 'persona'),
        room: a(2, 'sala'),
      };
  }
}

/**
 * ¿La pista es verdadera en esta rejilla completa?
 *
 * La semántica se escribe en forma existencial (y no asumiendo reglas latinas) para que
 * siga siendo correcta sobre rejillas que solo cumplen la regla de columna — así los
 * tests pueden comparar los dos modelos del MVP §1.
 */
export function clueHolds(input: Clue, grid: Grid): boolean {
  const n = grid.length;
  const parsed = parseClue(input, n);

  switch (parsed.kind) {
    case 'AT':
      return cellAt(grid, parsed.person, parsed.hour) === parsed.room;
    case 'NOT_AT':
      return cellAt(grid, parsed.person, parsed.hour) !== parsed.room;
    case 'OPEN':
      return cellAt(grid, parsed.person, 0) === parsed.room;
    case 'CLOSE':
      return cellAt(grid, parsed.person, n - 1) === parsed.room;
    case 'FIRST_IN': {
      const h = firstHourOccupied(grid, parsed.room, n);
      return h >= 0 && cellAt(grid, parsed.person, h) === parsed.room;
    }
    case 'LAST_IN': {
      const h = lastHourOccupied(grid, parsed.room, n);
      return h >= 0 && cellAt(grid, parsed.person, h) === parsed.room;
    }
    case 'JUST_BEFORE': {
      for (let h = 0; h < n - 1; h += 1) {
        if (
          cellAt(grid, parsed.first, h) === parsed.room &&
          cellAt(grid, parsed.second, h + 1) === parsed.room
        ) {
          return true;
        }
      }
      return false;
    }
    case 'BEFORE': {
      for (let h1 = 0; h1 < n - 1; h1 += 1) {
        if (cellAt(grid, parsed.first, h1) !== parsed.room) continue;
        for (let h2 = h1 + 1; h2 < n; h2 += 1) {
          if (cellAt(grid, parsed.second, h2) === parsed.room) return true;
        }
      }
      return false;
    }
  }
}

export function allCluesHold(clues: readonly Clue[], grid: Grid): boolean {
  return clues.every((c) => clueHolds(c, grid));
}

/**
 * ¿La pista dice **por sí sola** quién ocupa la celda `(hour, room)`?
 *
 * Existe para una sola cosa: que ninguna pista regale la celda del crimen. Si un caso
 * incluye «a las 23:00, Braulio estaba en el Sótano» y el cuerpo aparece en el Sótano a
 * las 23:00, el caso está resuelto antes de empezar. Ver `generateCase`.
 *
 * Cuentan como revelación:
 *
 * - `AT` sobre esa misma hora y sala.
 * - `OPEN` y `FIRST_IN` de esa sala cuando el crimen es a primera hora. Bajo reglas
 *   latinas ninguna sala queda vacía, así que la primera hora en que una sala se ocupa
 *   es siempre la 0: `FIRST_IN` equivale a `OPEN`.
 * - `CLOSE` y `LAST_IN` de esa sala cuando el crimen es a última hora.
 *
 * `NOT_AT` no cuenta: elimina a una persona, no nombra a ninguna. `BEFORE` y
 * `JUST_BEFORE` tampoco: fijan un orden relativo, no una hora concreta, así que hace
 * falta deducir. Eso es el juego, no un espóiler.
 */
export function revealsCell(
  input: Clue,
  n: number,
  cell: { hour: number; room: number },
): boolean {
  const parsed = parseClue(input, n);
  switch (parsed.kind) {
    case 'AT':
      return parsed.hour === cell.hour && parsed.room === cell.room;
    case 'OPEN':
    case 'FIRST_IN':
      return cell.hour === 0 && parsed.room === cell.room;
    case 'CLOSE':
    case 'LAST_IN':
      return cell.hour === n - 1 && parsed.room === cell.room;
    case 'NOT_AT':
    case 'BEFORE':
    case 'JUST_BEFORE':
      return false;
  }
}

export interface CluePoolOptions {
  /**
   * Tope de pistas `NOT_AT` en el pool. Son verdaderas pero débiles y hay `n²(n-1)`:
   * sin tope inundarían la mezcla y los casos se leerían como una lista de negaciones.
   * Por defecto `2n`.
   */
  maxNotAt?: number;
}

/**
 * Todas las pistas verdaderas para `solution`, por plantilla.
 *
 * `AT` se limita a las horas interiores: en la primera y la última hora ya existen
 * `OPEN`/`CLOSE`/`FIRST_IN`/`LAST_IN`, que dicen lo mismo y se leen mejor.
 */
export function buildCluePool(
  solution: Grid,
  rng: Rng,
  options: CluePoolOptions = {},
): Clue[] {
  const n = solution.length;
  const pool: Clue[] = [];

  const interior = n >= 3 ? { from: 1, to: n - 2 } : { from: 0, to: n - 1 };
  for (let p = 0; p < n; p += 1) {
    for (let h = interior.from; h <= interior.to; h += 1) {
      pool.push(clue.at(p, h, cellAt(solution, p, h)));
    }
    pool.push(clue.open(p, cellAt(solution, p, 0)));
    pool.push(clue.close(p, cellAt(solution, p, n - 1)));
  }

  for (let r = 0; r < n; r += 1) {
    // Bajo reglas latinas ninguna sala queda vacía, así que la primera hora ocupada de
    // cualquier sala es la 0 y la última la N-1.
    for (let p = 0; p < n; p += 1) {
      if (cellAt(solution, p, 0) === r) pool.push(clue.firstIn(p, r));
      if (cellAt(solution, p, n - 1) === r) pool.push(clue.lastIn(p, r));
    }

    const visitor: number[] = new Array<number>(n).fill(-1);
    for (let p = 0; p < n; p += 1) {
      const h = hourOf(solution, p, r);
      if (h >= 0) visitor[h] = p;
    }
    for (let h1 = 0; h1 < n; h1 += 1) {
      const first = visitor[h1] as number;
      for (let h2 = h1 + 1; h2 < n; h2 += 1) {
        const second = visitor[h2] as number;
        pool.push(clue.before(first, second, r));
        if (h2 === h1 + 1) pool.push(clue.justBefore(first, second, r));
      }
    }
  }

  const notAt: Clue[] = [];
  for (let p = 0; p < n; p += 1) {
    for (let h = 0; h < n; h += 1) {
      const actual = cellAt(solution, p, h);
      for (let r = 0; r < n; r += 1) {
        if (r !== actual) notAt.push(clue.notAt(p, h, r));
      }
    }
  }
  const maxNotAt = options.maxNotAt ?? 2 * n;
  pool.push(...rng.shuffle(notAt).slice(0, Math.max(0, maxNotAt)));

  const seen = new Set<string>();
  return pool.filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)));
}
